import type { WritableAtom } from 'jotai';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import { onboardingTours } from './config';
import { type PiqueOnboardingStep, STORAGE_KEYS } from './types';

const _onboardingStepIndexAtom = atomWithStorage<number>(
  STORAGE_KEYS.ONBOARDING_STEP_INDEX,
  0
);

export const onboardingStepIndexAtom: WritableAtom<number, [number], void> =
  atom(
    get => get(_onboardingStepIndexAtom),
    (_get, set, value: number) => set(_onboardingStepIndexAtom, value)
  );

const _postAugmentationStepIndexAtom = atomWithStorage<number>(
  STORAGE_KEYS.POST_AUGMENTATION_STEP_INDEX,
  0
);

export const postAugmentationStepIndexAtom: WritableAtom<
  number,
  [number],
  void
> = atom(
  get => get(_postAugmentationStepIndexAtom),
  (_get, set, value: number) => set(_postAugmentationStepIndexAtom, value)
);

export const MAIN_TOUR_TOTAL_STEPS =
  onboardingTours.find(t => t.id === 'pique-intro')?.steps.length ?? 5;

const POST_AUGMENTATION_STEPS_RAW: PiqueOnboardingStep[] = [
  {
    id: 'explore-mindmap',
    target: '[data-testid="edgeless-mindmap-view"]',
    title: 'Explore your mind map!',
    content:
      "This is Pique's visual overview of the conversation. Each node is timestamped and linked back to the transcript.",
    placement: 'right',
    disableBeacon: true,
  },
  {
    id: 'notes-side',
    target: 'div.affine-list-block__prefix[contenteditable="false"]',
    title: 'The Notes Side',
    content:
      'This is where your written notes appear—neatly captured and easy to review.',
    placement: 'left',
    disableBeacon: true,
    floaterProps: {
      offset: 80,
    },
  },
  {
    id: 'action-items',
    target: 'affine-data-view-kanban-card',
    title: 'Action items captured',
    content:
      'Pique identifies and pulls out key tasks automatically and organizes them here.',
    placement: 'top-start',
    disableBeacon: true,
    requiresInteraction: true,
    actionText: 'Click {{openIcon}} on this action item to continue...',
    floaterProps: {
      offset: 12,
    },
  },
  {
    id: 'back-button',
    target: '[data-testid="desktop-back-button"]',
    title: 'Back button',
    content: 'Takes you to the previous page.',
    placement: 'bottom',
    disableBeacon: true,
    requiresInteraction: true,
    actionText: 'Click the "back" arrow to return to the Dashboard',
    isTauriOnly: true,
  },
  {
    id: 'action-items-overview',
    target: '[data-onboarding-action-group-index="0"]',
    title: 'View all your tasks',
    content:
      'All action items are automatically organized and tracked by Pique here.',
    placement: 'left',
    disableBeacon: true,
  },
];

export const getPostAugmentationSteps = (): PiqueOnboardingStep[] => {
  return POST_AUGMENTATION_STEPS_RAW.filter(step => {
    if (step.isTauriOnly && !environment.isTauri) {
      return false;
    }
    return true;
  });
};

export const POST_AUGMENTATION_TOTAL_STEPS = getPostAugmentationSteps().length;

export interface TourProgress {
  currentStep: number;
  totalSteps: number;
  isComplete: boolean;
  hasStarted: boolean;
}

export const getMainTourProgress = (): TourProgress => {
  const stepIndex = parseInt(
    localStorage.getItem(STORAGE_KEYS.ONBOARDING_STEP_INDEX) ?? '0',
    10
  );
  const isComplete =
    localStorage.getItem(STORAGE_KEYS.ONBOARDING_SHOWN) === 'true';
  return {
    currentStep: isComplete ? MAIN_TOUR_TOTAL_STEPS : stepIndex,
    totalSteps: MAIN_TOUR_TOTAL_STEPS,
    isComplete,
    hasStarted: stepIndex > 0 || isComplete,
  };
};

export const getPostAugmentationTourProgress = (): TourProgress => {
  const stepIndex = parseInt(
    localStorage.getItem(STORAGE_KEYS.POST_AUGMENTATION_STEP_INDEX) ?? '0',
    10
  );
  const isComplete =
    localStorage.getItem(STORAGE_KEYS.POST_AUGMENTATION_SHOWN) === 'true';
  return {
    currentStep: isComplete ? POST_AUGMENTATION_TOTAL_STEPS : stepIndex,
    totalSteps: POST_AUGMENTATION_TOTAL_STEPS,
    isComplete,
    hasStarted: stepIndex > 0 || isComplete,
  };
};

export const isAnyTourInProgress = (): boolean => {
  const mainProgress = getMainTourProgress();
  const postAugProgress = getPostAugmentationTourProgress();
  return (
    (mainProgress.hasStarted && !mainProgress.isComplete) ||
    (postAugProgress.hasStarted && !postAugProgress.isComplete)
  );
};

export const areAllToursComplete = (): boolean => {
  const mainProgress = getMainTourProgress();
  const postAugProgress = getPostAugmentationTourProgress();
  return mainProgress.isComplete && postAugProgress.isComplete;
};
